import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  DisplayText,
  Illus404,
  Image,
  LoadingAnimation,
  theme,
} from '@sg/garnish';

import { useLastInteractedStore } from '../../context';
import { useCustomizeBaseProductProductForRestaurantQuery } from './graphql/CustomizeBaseProduct.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizeBaseProductScreen = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const lastInteractedStore = useLastInteractedStore();

  const { baseProductSlug, redirectTo, sharedLineItemId } = useLocalSearchParams<RouteParams>();

  // ─── State ───────────────────────────────────────────────────────────

  // NOTE: Last interacted store can populate after the first render, so wait one tick before
  //       treating a missing location as the real empty state.
  const [hasCheckedInitialLocation, setHasCheckedInitialLocation] = useState(false);

  // ─────────────────────────────────────────────────────────────────────

  const lastInteractedStoreId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [productResponse] = useCustomizeBaseProductProductForRestaurantQuery({
    pause: !baseProductSlug || !lastInteractedStore?.restaurantId,
    variables: { baseProductSlug, restaurantId: lastInteractedStoreId },
    requestPolicy: 'network-only',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const restaurantId = lastInteractedStore?.restaurantId;
  const deliveryAddressId = lastInteractedStore?.addressId;

  const hasLocation = Boolean(restaurantId);
  const hasResolvedLocation = hasLocation || hasCheckedInitialLocation;
  const productForRestaurant = productResponse.data?.productForRestaurant;

  const isFetchingProduct = productResponse.fetching || productResponse.stale;
  const isUnavailableProduct = hasLocation && !isFetchingProduct && (!productForRestaurant || productForRestaurant.outOfStock); // prettier-ignore
  const hasShareRedirect = checkIfShareRedirect(redirectTo);
  const shouldDismissToShare = isUnavailableProduct && hasShareRedirect;

  // ─── Handlers ────────────────────────────────────────────────────────

  const navigateToLocations = useCallback(() => {
    router.dismissTo('/locations');
  }, [router]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (hasCheckedInitialLocation || hasLocation) return;

    setHasCheckedInitialLocation(true);
  }, [hasCheckedInitialLocation, hasLocation]);

  useEffect(() => {
    if (!isUnavailableProduct || !hasShareRedirect) return;

    router.dismissTo(redirectTo);
  }, [isUnavailableProduct, redirectTo, router, hasShareRedirect]);

  // ─────────────────────────────────────────────────────────────────────

  if (!hasResolvedLocation || (hasLocation && isFetchingProduct) || shouldDismissToShare) {
    return <LoadingAnimation backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  // ─── Redirect ────────────────────────────────────────────────────────

  if (hasLocation && !isUnavailableProduct && productForRestaurant) {
    const productSlug = productForRestaurant.slug;
    const restaurantSlug = productForRestaurant.restaurant.slug;

    if (deliveryAddressId) {
      return (
        <Redirect
          href={{
            pathname: '/delivery/[addressId]/[productSlug]',
            params: {
              addressId: deliveryAddressId,
              productSlug,
              ...(sharedLineItemId ? { sharedLineItemId } : {}),
            },
          }}
        />
      );
    }

    return (
      <Redirect
        href={{
          pathname: '/[restaurantSlug]/[productSlug]',
          params: {
            productSlug,
            restaurantSlug,
            ...(sharedLineItemId ? { sharedLineItemId } : {}),
          },
        }}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView style={styles.outerContainer} contentContainerStyle={styles.content}>
      <View style={styles.container}>
        <Image
          source={Illus404}
          alt={formatMessage(messages.illustrationAlt)}
          style={styles.illustration}
          contentFit="contain"
        />

        <DisplayText sizeMatch={['32']} style={styles.title}>
          <FormattedMessage {...messages.title} />
        </DisplayText>

        <BodyText sizeMatch={['18']} style={styles.description}>
          {isUnavailableProduct ? (
            <FormattedMessage {...messages.unavailableProduct} />
          ) : (
            <FormattedMessage {...messages.description} />
          )}
        </BodyText>

        <Button size="large" onPress={navigateToLocations} style={styles.button}>
          <FormattedMessage {...messages.cta} />
        </Button>
      </View>
    </ScrollView>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfShareRedirect(redirectTo: string | undefined): redirectTo is ShareRedirect {
  return redirectTo?.startsWith('/share/') === true;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing['20'],
    paddingHorizontal: theme.spacing['6'],
  },
  container: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
  illustration: {
    width: 250,
    height: 250,
  },
  title: {
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
  },
  button: {
    marginTop: theme.spacing['10'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  illustrationAlt: {
    defaultMessage: 'An empty bowl illustration',
    description: 'Customize base product | Not found illustration alternative text',
  },
  title: {
    defaultMessage: 'Not available',
    description: 'Customize base product | Not available title',
  },
  description: {
    defaultMessage: 'Select a location to start ordering.',
    description: 'Customize base product | Select location description',
  },
  unavailableProduct: {
    defaultMessage: 'This product is not available at your selected location.',
    description: 'Customize base product | Unavailable product message',
  },
  cta: {
    defaultMessage: 'Change location',
    description: 'Customize base product | Change location CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  baseProductSlug: string;
  redirectTo?: string;
  sharedLineItemId?: string;
};

type ShareRedirect = Href & `/share/${string}`;
